
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useEffect, useRef, useState } from 'react';

import { interactionService } from '../../services/ui-interactions/interaction-service';
import { SvgActionMenuConfig } from './types/svg-action-menu-config.type';
import { SvgActionMenuItem } from './svg-action-menu-item';
import { svgActionMenuOptionDefaults } from './svg-action-menu-option-defaults';
import { useSvgTooltip } from '../svg-tooltip/use-svg-tooltip';

export type AddActionMenuProps = {
	items: SvgActionMenuConfig;
	angleCorrection: number;
	x: number;
	y: number;
};

export const SvgActionMenu = (props: AddActionMenuProps): React.ReactElement => {
	const {
		x,
		y,
		angleCorrection,
		items: { key, icon, items, menuOptions }
	} = props;

	// extract config
	const { itemButtonRadius, itemButtonPadding, menuButtonPadding, menuButtonRadius, menuTooltipPadding } = {
		...svgActionMenuOptionDefaults,
		...menuOptions
	};

	const [hover, setHover] = useState(false);
	const [hidden, setHidden] = useState(false);

	const container = useRef(null);
	const refButton = useRef(null);

	// tooltip position
	const xTooltip = 0;
	const yTooltip = menuButtonRadius + menuButtonPadding + itemButtonRadius * 2 + menuTooltipPadding;
	const { setTooltip, clearTooltip, renderTooltip } = useSvgTooltip({ cx: xTooltip, cy: yTooltip });

	// hover animations
	useGSAP(() => {
		// animate menu item
		gsap.to(refButton.current, {
			opacity: hover ? 1 : 0.45,
			duration: 0.5,
			ease: 'power1.out'
		});

		gsap.to(container.current, {
			r: hover ? menuButtonRadius + menuButtonPadding + itemButtonRadius * 2 : menuButtonRadius
		});
	}, [hover]);

	useEffect(() => {
		if (hover) {
			const activeItems = items.filter((item) => !item.hidden || !item.hidden());
			if (activeItems.length === 0) setTooltip(key, 'No Actions Available');
		} else {
			clearTooltip(key);
		}
	}, [hover]);

	useEffect(() => {
		const sub = interactionService.stream.subscribe(() => {
			// using the service indicator because it's based on distance. the stream is set on drag start which is initiated on mouse down
			setHidden(interactionService.isDragging);
		});
		return () => sub.unsubscribe();
	}, []);

	// remove menu while dragging
	// console.log('mouse.isDragging', mouse.isDragging, mouse.drag);
	if (hidden) return null;

	return (
		<g transform={`translate(${x} ${y}) rotate(${angleCorrection} 0 0)`} key={key} pointerEvents="all" onMouseLeave={() => setHover(false)}>
			<circle ref={container} cx={0} cy={0} r={menuButtonRadius} fill="none" strokeWidth=".125" stroke="none" />

			{/* menu items */}
			<g fill="gray" stroke="lightgreen" strokeWidth=".25">
				{items
					.filter((item) => !item.hidden || !item.hidden())
					.map((item, i) => {
						return (
							<SvgActionMenuItem
								key={i}
								{...{
									index: i,
									active: hover,
									totalItems: items.length,
									setTooltip,
									clearTooltip,
									item,
									menuOptions: {
										...svgActionMenuOptionDefaults,
										...menuOptions
									}
								}}
							/>
						);
					})}
			</g>

			{/* menu button */}
			<g className={`action-button-${key}`} onMouseEnter={() => setHover(true)}>
				<circle ref={refButton} cx={0} cy={0} r={menuButtonRadius} fill="white" />
				<circle ref={refButton} cx={0} cy={0} r={menuButtonRadius} fill="lightgreen" opacity={hover ? 1 : 0.45} strokeWidth=".125" stroke="darkgreen" />
				{icon({ cx: 0, cy: 0, r: menuButtonRadius * 0.5, opacity: hover ? 1 : 0.45 })}
			</g>

			{/* tooltip */}
			{renderTooltip}
		</g>
	);
};
